import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Euro, Clock, Users, HardHat,
  ChevronDown, ChevronUp, Calendar, Calculator, AlertTriangle,
  CheckCircle, RefreshCw, Settings, PieChart, BarChart3, Target
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function RentabiliteDashboard() {
  const { user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [expandedChantier, setExpandedChantier] = useState(null);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchRentabiliteData();
  }, [dateRange]);

  const fetchRentabiliteData = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    setLoading(true);
    try {
      const now = new Date();
      let startDate;
      
      switch (dateRange) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      const url = `${API_URL}/api/pointage/rentabilite?start_date=${startDate.toISOString().split('T')[0]}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setData(await response.json());
      } else {
        toast.error('Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getRentabilityColor = (level) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-amber-600 bg-amber-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getRentabilityLabel = (level) => {
    switch (level) {
      case 'high': return 'Excellente';
      case 'medium': return 'Correcte';
      case 'low': return 'Faible';
      default: return 'N/A';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatHours = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    return `${Math.round(hours)}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rentabilite-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-rm-primary" />
          <div>
            <h1 className="text-xl font-bold text-rm-text">Tableau de bord Rentabilité</h1>
            <p className="text-sm text-slate-500">Analyse des marges basée sur les heures pointées</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2"
            data-testid="date-range-select"
          >
            <option value="week">7 derniers jours</option>
            <option value="month">30 derniers jours</option>
            <option value="quarter">3 derniers mois</option>
            <option value="year">12 derniers mois</option>
          </select>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)}>
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={fetchRentabiliteData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4"
        >
          <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Paramètres de calcul
          </h3>
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm text-blue-700 block mb-1">Taux horaire moyen</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseInt(e.target.value) || 0)}
                  className="w-20 px-2 py-1 border border-blue-200 rounded text-sm"
                />
                <span className="text-sm text-blue-600">€/heure</span>
              </div>
            </div>
            <p className="text-xs text-blue-600 flex-1">
              Ce taux est utilisé pour calculer le coût de la main d'œuvre. 
              Dans une version future, tu pourras définir un taux par employé.
            </p>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Euro className="w-4 h-4" />
              <span className="text-xs">Chiffre d'affaires</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">
              {formatCurrency(data.summary.total_revenue)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Calculator className="w-4 h-4" />
              <span className="text-xs">Coûts totaux</span>
            </div>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(data.summary.total_cost)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">Marge totale</span>
            </div>
            <p className={`text-2xl font-bold ${data.summary.total_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(data.summary.total_margin)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Target className="w-4 h-4" />
              <span className="text-xs">Marge moyenne</span>
            </div>
            <p className={`text-2xl font-bold ${data.summary.average_margin_percent >= 20 ? 'text-green-600' : data.summary.average_margin_percent >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
              {data.summary.average_margin_percent}%
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Heures pointées</span>
            </div>
            <p className="text-2xl font-bold text-rm-primary">
              {formatHours(data.summary.total_hours_tracked)}
            </p>
          </div>
        </div>
      )}

      {/* Workers Summary */}
      {data?.workers && data.workers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-rm-text flex items-center gap-2">
              <Users className="w-5 h-5 text-rm-primary" />
              Répartition par employé
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
            {data.workers.map((worker) => (
              <div key={worker.user_id} className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium text-rm-text truncate">{worker.name}</p>
                <p className="text-xs text-slate-400">{worker.rate}€/h</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-slate-500">{formatHours(worker.hours)}</span>
                  <span className="text-xs text-red-500">{formatCurrency(worker.cost || 0)}</span>
                </div>
                <p className="text-xs text-slate-400">{worker.chantiers} chantier(s)</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chantiers List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-rm-text flex items-center gap-2">
            <HardHat className="w-5 h-5 text-rm-primary" />
            Rentabilité par chantier
          </h2>
          <span className="text-xs text-slate-500">
            Trié par marge (plus faible en premier)
          </span>
        </div>

        <div className="divide-y divide-slate-100">
          {data?.chantiers?.length > 0 ? (
            data.chantiers.map((chantier) => (
              <div key={chantier.id} className="hover:bg-slate-50 transition-colors">
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedChantier(expandedChantier === chantier.id ? null : chantier.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRentabilityColor(chantier.rentability_calculated)}`}>
                        {chantier.rentability_calculated === 'high' ? (
                          <TrendingUp className="w-5 h-5" />
                        ) : chantier.rentability_calculated === 'low' ? (
                          <TrendingDown className="w-5 h-5" />
                        ) : (
                          <TrendingUp className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-rm-text">{chantier.client_name}</p>
                        <p className="text-xs text-slate-500">{chantier.address}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Devis</p>
                        <p className="font-medium">{formatCurrency(chantier.quote_amount)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Coût</p>
                        <p className="font-medium text-red-600">{formatCurrency(chantier.total_cost_calculated)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Marge</p>
                        <p className={`font-bold ${chantier.margin_calculated >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(chantier.margin_calculated)}
                        </p>
                      </div>
                      <div className="text-right w-20">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRentabilityColor(chantier.rentability_calculated)}`}>
                          {chantier.margin_percent}%
                        </span>
                      </div>
                      {expandedChantier === chantier.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedChantier === chantier.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-4 pb-4 border-t border-slate-100"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      {/* Breakdown */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-medium text-sm text-rm-text mb-3">Décomposition des coûts</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Coûts enregistrés</span>
                            <span className="font-medium">{formatCurrency(chantier.actual_cost || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Main d'œuvre (pointages)</span>
                            <span className="font-medium">{formatCurrency(chantier.labor_cost_tracked)}</span>
                          </div>
                          <div className="border-t border-slate-200 pt-2 flex justify-between font-semibold">
                            <span>Total</span>
                            <span className="text-red-600">{formatCurrency(chantier.total_cost_calculated)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Workers */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-medium text-sm text-rm-text mb-3">Heures par employé</h4>
                        {chantier.workers_breakdown?.length > 0 ? (
                          <div className="space-y-2">
                            {chantier.workers_breakdown.map((worker) => (
                              <div key={worker.user_id} className="flex justify-between items-center text-sm">
                                <div>
                                  <span className="text-slate-600">{worker.name}</span>
                                  <span className="text-xs text-slate-400 ml-2">
                                    ({worker.rate}€/h)
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="font-medium">{formatHours(worker.hours)}</span>
                                  <span className="text-xs text-red-500 ml-2">
                                    {formatCurrency(worker.cost || 0)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">Aucun pointage enregistré</p>
                        )}
                      </div>

                      {/* Status & Dates */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-medium text-sm text-rm-text mb-3">Informations</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Statut</span>
                            <span className="font-medium capitalize">{chantier.status?.replace('_', ' ')}</span>
                          </div>
                          {chantier.planned_start_date && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Début prévu</span>
                              <span>{new Date(chantier.planned_start_date).toLocaleDateString('fr-FR')}</span>
                            </div>
                          )}
                          {chantier.planned_end_date && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Fin prévue</span>
                              <span>{new Date(chantier.planned_end_date).toLocaleDateString('fr-FR')}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-slate-600">Heures totales</span>
                            <span className="font-medium">{formatHours(chantier.tracked_hours)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Alert for low margin */}
                    {chantier.rentability_calculated === 'low' && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-800">Attention : rentabilité faible</p>
                          <p className="text-sm text-red-600 mt-1">
                            Ce chantier a une marge de seulement {chantier.margin_percent}%. 
                            Vérifiez si des heures supplémentaires non prévues ont été effectuées 
                            ou si des coûts matériaux ont dépassé le budget.
                          </p>
                        </div>
                      </div>
                    )}

                    {chantier.rentability_calculated === 'high' && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-green-800">Excellente rentabilité</p>
                          <p className="text-sm text-green-600 mt-1">
                            Ce chantier a une marge de {chantier.margin_percent}%, ce qui est supérieur à l'objectif de 30%.
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-500">
              <HardHat className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun chantier avec des pointages sur cette période</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
